export interface FilterDescriptionConfig<T = any> {
  key: string;
  labelSingular: string;
  labelPlural: string;
  canIncludeNA?: boolean;
  stringIds?: boolean;
  sourceList?: T[];
  name?: string;
  pmsNames?: string;
}

export function buildFilterDescriptionEntry<T = any>(
  routeParams: Record<string, string>,
  config: FilterDescriptionConfig<T>,
  pushTo: (entry: { name: string; tooltip?: string; additional?: number }) => void,
  additionalExcluded: string[] = []
): void {
  const rawValue = routeParams[config.key];
  if (!rawValue) return;

  const rawIds = rawValue
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);

  const includesEventHandler = rawIds.includes('EventHandler');

  const useStringIds = config.stringIds || rawIds.some(id => isNaN(Number(id)) && id !== '-1');
  const ids = useStringIds
    ? rawIds.filter(id => id !== '-1' && id !== 'EventHandler')
    : rawIds
        .filter(id => id !== '-1')
        .map(id => Number(id))
        .filter(id => !isNaN(id));

  const excludedKeys = [...additionalExcluded];

  const nameKeyBase = config.key
    .replace(/Ids?$/, '')
    .replace(/Types$/, 'Type')
    .replace(/Kinds$/, 'Kind');
  const overrideKey = `${nameKeyBase}Names`;
  const overrideSingleKey = `${nameKeyBase}Name`;

  const nameOverride = !excludedKeys.includes(config.key)
    ? routeParams[overrideKey] || routeParams[overrideSingleKey]
    : null;

  let names: string[] = [];
  let actualCount = ids.length;

  if (nameOverride) {
    const andMoreMatch = nameOverride.match(/, and (\d+) more$/);
    if (andMoreMatch) {
      const moreCount = parseInt(andMoreMatch[1], 10);
      const firstPart = nameOverride.replace(/, and \d+ more$/, '').trim();
      names = [firstPart];
      actualCount = ids.length;
    } else {
      names = nameOverride
        .split(',')
        .map(name => name.trim())
        .filter(Boolean);
      actualCount = ids.length > 0 ? ids.length : names.length;
    }
  } else {
    names = ids.map((id: string | number) => (config.name ? config.name : id.toString())).filter(Boolean);
    actualCount = ids.length;
  }

  if (includesEventHandler && config.pmsNames) {
    names.unshift(config.pmsNames);
    actualCount += 1;
  }

  const totalCount = actualCount;
  if (totalCount === 0) return;

  const [first] = names;
  const tooltip = names.join('\n');
  const additional = totalCount > 1 ? totalCount - 1 : undefined;

  let labelSingular = config.labelSingular;
  let labelPlural = config.labelPlural;
  if (config.pmsNames) {
    labelSingular = labelSingular.replace('{PMS}', config.pmsNames);
    labelPlural = labelPlural.replace('{PMS}', config.pmsNames);
  }

  pushTo({
    name: totalCount > 1 ? `${labelPlural}: ${first}` : `${labelSingular}: ${first}`,
    ...(additional !== undefined && { tooltip, additional }),
  });
}
